
import React, { useState } from 'react';
import { LOGO } from '../constants';
import { ShoppingCart, User, LogOut, Package, Menu, X, Info, ChevronDown, Globe, Coins, Heart, RefreshCw } from 'lucide-react';
import { User as UserType } from '../types';
import FloatingChat from './FloatingChat';
import SocialProofPopup from './SocialProofPopup';
import { Language, translations } from '../lib/i18n';
import { useStoreContext } from '../contexts/StoreContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { SUPPORTED_CURRENCIES } from '../lib/currencies';

interface LayoutProps {
  children: React.ReactNode;
  cartCount: number;
  user: UserType | null;
  onNavigate: (page: any) => void;
  onLogout: () => void;
  lang: Language;
  onLangChange: (lang: Language) => void;
}

const StorefrontLayout: React.FC<LayoutProps> = ({ children, cartCount, user, onNavigate, onLogout, lang, onLangChange }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const { store } = useStoreContext();
  const { currentCurrency, setCurrency } = useCurrency();
  const t = translations[lang];

  if (!store) return null;

  const branding = store.branding;
  const isGlass = branding?.glassmorphism;

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-500 ${branding?.themeMode === 'dark' ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="bg-brand-primary text-white text-[10px] font-bold py-1 px-4 flex items-center justify-center gap-2 uppercase tracking-widest transition-colors duration-500">
        <Info className="h-3 w-3" />
        Lynqar Exploration Mode: Experimental UI Features Active.
      </div>

      <nav className={`sticky top-0 z-50 transition-all duration-500 border-b ${
        isGlass 
        ? 'bg-white/70 backdrop-blur-2xl border-white/20 shadow-lg' 
        : branding?.themeMode === 'dark' ? 'bg-gray-900/80 backdrop-blur-md border-gray-800 shadow-sm' : 'bg-white border-gray-200 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center gap-8">
              <button onClick={() => onNavigate('home')} className="flex-shrink-0">
                {LOGO}
              </button>
              <div className="hidden md:flex items-center space-x-2">
                <button onClick={() => onNavigate('home')} className="px-4 py-2 text-sm font-bold transition-colors hover:text-brand-primary">{t.home}</button>
                <button className="px-4 py-2 text-sm font-bold transition-colors hover:text-brand-primary">{t.gadgets}</button>
                <button onClick={() => onNavigate('subscriptions')} className="px-4 py-2 text-sm font-bold transition-colors text-blue-600 hover:text-blue-700 flex items-center gap-2">
                   <RefreshCw className="h-3 w-3" /> Tech Subs
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Currency Selector */}
              <div className="relative">
                <button 
                  onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-brand text-[10px] font-black uppercase transition-all border ${isGlass ? 'bg-white/40 border-white/20' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'}`}
                >
                  <Globe className="h-3 w-3" /> {currentCurrency.code} <ChevronDown className={`h-3 w-3 transition-transform ${isCurrencyOpen ? 'rotate-180' : ''}`} />
                </button>
                {isCurrencyOpen && (
                  <div className={`absolute top-full right-0 mt-2 w-48 rounded-brand shadow-2xl border py-2 z-[60] animate-in slide-in-from-top-2 ${branding?.themeMode === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                    {SUPPORTED_CURRENCIES.map(curr => (
                      <button 
                        key={curr.code}
                        onClick={() => { setCurrency(curr.code); setIsCurrencyOpen(false); }}
                        className="w-full text-left px-4 py-2 text-[10px] font-black uppercase tracking-widest flex items-center justify-between hover:bg-brand-primary/10 transition-colors"
                      >
                        <span>{curr.name}</span>
                        <span className="text-brand-primary">{curr.symbol}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Language Switcher */}
              <div className={`flex items-center rounded-brand p-1 border transition-all ${isGlass ? 'bg-white/40 border-white/20' : 'bg-gray-50 border-gray-100'}`}>
                <button 
                  onClick={() => onLangChange('en')}
                  className={`px-3 py-2 rounded-brand text-[10px] font-black uppercase transition-all ${lang === 'en' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  EN
                </button>
                <button 
                  onClick={() => onLangChange('hi')}
                  className={`px-3 py-2 rounded-brand text-[10px] font-black uppercase transition-all ${lang === 'hi' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  हिं
                </button>
              </div>

              <button onClick={() => onNavigate('wishlist')} className="p-3 hover:bg-black/5 rounded-brand transition-all">
                <Heart className="h-6 w-6 text-gray-500" />
              </button>

              <button onClick={() => onNavigate('cart')} className="relative p-3 hover:bg-black/5 rounded-brand transition-all">
                <ShoppingCart className="h-6 w-6" />
                {cartCount > 0 && (
                  <span className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-1 text-[10px] font-black bg-brand-primary text-white rounded-full transition-colors duration-500">
                    {cartCount}
                  </span>
                )}
              </button>

              <div className="h-6 w-px bg-gray-200 mx-2 hidden md:block" />

              {user ? (
                <div className="relative">
                  <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-2 p-2 rounded-brand hover:bg-black/5 transition-all">
                    <div className="w-10 h-10 rounded-brand bg-brand-primary text-white flex items-center justify-center font-black transition-colors duration-500">
                      {user.email[0].toUpperCase()}
                    </div>
                  </button>
                  {isUserMenuOpen && (
                    <div className={`absolute right-0 mt-2 w-56 rounded-brand shadow-2xl border py-2 animate-in slide-in-from-top-2 ${branding?.themeMode === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                      <div className="px-4 py-3 border-b border-gray-50 mb-1">
                         <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Profile</p>
                         <p className="text-sm font-bold truncate">{user.email}</p>
                      </div>
                      <button onClick={() => { onNavigate('my-orders'); setIsUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-700 hover:bg-brand-primary/10">
                        <Package className="h-5 w-5 text-brand-primary" /> {t.myOrders}
                      </button>
                      <button onClick={() => { onNavigate('subscriptions'); setIsUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-700 hover:bg-brand-primary/10">
                        <RefreshCw className="h-5 w-5 text-brand-primary" /> My Subscriptions
                      </button>
                      <button onClick={() => { onNavigate('wishlist'); setIsUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-700 hover:bg-brand-primary/10">
                        <Heart className="h-5 w-5 text-brand-primary" /> My Wishlist
                      </button>
                      {(user.role === 'ADMIN' || user.role === 'SELLER' || user.role === 'STAFF') && (
                        <button onClick={() => { onNavigate('admin'); setIsUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-gray-700 hover:bg-brand-primary/10">
                          <User className="h-5 w-5 text-brand-primary" /> {t.adminPanel}
                        </button>
                      )}
                      <button onClick={() => { onLogout(); setIsUserMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50">
                        <LogOut className="h-5 w-5" /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => onNavigate('login')} className="bg-brand-primary text-white px-6 py-3 rounded-brand text-sm font-bold hover:opacity-90 transition-all shadow-lg shadow-brand-primary/20">
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className={`flex-grow ${branding?.themeMode === 'dark' ? 'bg-gray-950' : 'bg-gray-50/50'}`}>
        {children}
      </main>

      <footer className={`${branding?.themeMode === 'dark' ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'} border-t py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 text-center md:text-left">
            <div className="space-y-6">
              {LOGO}
              <p className="text-gray-500 text-sm">The only premium gadget marketplace that scales with your ambition. Exploration Mode V2.</p>
            </div>
            <div>
              <h4 className="font-black mb-6 uppercase tracking-widest text-xs">Explore</h4>
              <ul className="text-gray-500 text-sm space-y-4">
                <li><button onClick={() => onNavigate('home')}>Storefront</button></li>
                <li><button onClick={() => onNavigate('admin')}>Merchant Dashboard</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-black mb-6 uppercase tracking-widest text-xs">Tech</h4>
              <ul className="text-gray-500 text-sm space-y-4">
                <li>Visual AI Search</li>
                <li>Dynamic Theme Engine</li>
                <li>Lynq Coins Loyalty</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-8 rounded-brand space-y-4 dark:bg-gray-800">
              <h4 className="font-bold">Beta Updates</h4>
              <p className="text-xs text-gray-400">Get early access to exploration features.</p>
              <button className="w-full bg-brand-primary text-white py-3 rounded-brand text-sm font-bold transition-colors">Join Waitlist</button>
            </div>
          </div>
        </div>
      </footer>

      <FloatingChat user={user} lang={lang} />
      <SocialProofPopup storeId={store.id} />
    </div>
  );
};

export default StorefrontLayout;
